<script lang="ts" setup generic="T extends BasicForm.BaseData">
import type { VNode } from 'vue';
import { computed, toValue, watch } from 'vue';
import { type FormProps } from 'naive-ui';
import _ from 'lodash';
import { ShowWhenDisabledComponents } from '@/constants/common';
import type { BasicForm, BasicFormExpose, BasicFormProps } from '@/typings/basic-form';
import { useBasicForm } from './use-form';

/** 自定义表单组件 */
defineOptions({
  name: 'BasicForm'
});
const props = withDefaults(defineProps<BasicFormProps<T>>(), {
  operation: 'view',
  showTitle: true,
  showActions: false,
  isEditOriginalData: false,
  formConfig: () => {
    return {};
  }
});
const emit = defineEmits<{
  submit: [Partial<T> | null];
}>();
/** 显示状态 */
const show = defineModel('show', { type: Boolean, required: true });
/** 表单配置 */
const finalFormConfig = computed<FormProps>(() => {
  return {
    labelPlacement: 'left',
    labelAlign: 'right',
    labelWidth: 125,
    requireMarkPlacement: 'left',
    showRequireMark: props.operation === 'view' ? false : undefined,
    ...props.formConfig
  };
});

const {
  loading,
  startLoading,
  endLoading,
  action,
  formRef,
  validate,
  restoreValidation,
  model,
  // shallowModel,
  originalModel,
  formFields,
  groups,
  initModel,
  getSubmitModel
} = useBasicForm({
  show,
  props
});

watch(
  () => props.editData?.id,
  () => {
    initModel();
  }
);

async function handleSubmit() {
  try {
    await validate();
    emit('submit', model.value);
  } catch {
    // window.$message?.error(error);
  }
}
defineExpose<BasicFormExpose<T>>({
  show,
  model,
  originalModel,
  loading,
  action,
  initModel,
  startLoading,
  endLoading,
  formRef,
  getSubmitModel,
  validate,
  restoreValidation
});
</script>

<template>
  <NSpin :show="loading" class="h-full w-full" :description="loading ? '加载中...' : ''">
    <NForm
      ref="formRef"
      :model="model"
      v-bind="finalFormConfig"
      :disabled="operation === 'view'"
      :class="[operation === 'view' ? 'form-view' : '']"
    >
      <slot name="header" :model="model" :loading="loading">
        <NSkeleton
          v-if="showTitle && loading && action === 'fetch'"
          :sharp="false"
          width="60%"
          height="48px"
          class="mb-20px"
        ></NSkeleton>
        <div v-else-if="showTitle" class="flex items-center justify-between">
          <NH2>
            {{ title ? (typeof title === 'string' ? title : title({ data: model, operation })) : '' }}
          </NH2>
          <div class="mb-20px flex items-center justify-end">
            <slot name="header-action" :model="model" :loading="loading"></slot>
          </div>
        </div>
      </slot>
      <template v-if="groups != null">
        <template v-for="(group, groupIndex) in groups" :key="group.key">
          <template v-if="group.shallowIfShow">
            <NH3 v-if="group.title" prefix="bar">
              {{ group.title }}
            </NH3>
            <NGrid responsive="screen" x-gap="12px" item-responsive>
              <template v-for="field in group.fields" :key="field.cacheKey">
                <NFormItemGi
                  v-if="field.shallowIfShow"
                  :key="field.key"
                  :span="field.shallowColSpan"
                  :label="field.title"
                  :label-placement="'labelPlacement' in field ? field.labelPlacement : undefined"
                  :label-align="'labelAlign' in field ? field.labelAlign : undefined"
                  :label-width="'labelWidth' in field ? field.labelWidth : undefined"
                  :label-style="'labelStyle' in field ? field.labelStyle : undefined"
                  :path="toValue(field.rulePath)"
                  :show-label="field.showLabel"
                  :show-feedback="'showFeedback' in field ? field.showFeedback : true"
                  :rule="toValue(field.shallowRules)"
                >
                  <NSkeleton v-if="loading && action === 'fetch'" :sharp="false" width="100%" height="32px"></NSkeleton>
                  <template v-else-if="show">
                    <slot
                      :name="'field-' + field.key"
                      :model="model"
                      :original-model="originalModel"
                      :loading="loading"
                      :operation="operation"
                      :value="_.get(model, field.key)"
                    >
                      <template v-if="field.type !== 'slot'">
                        <component
                          :is="field.shallowRender"
                          v-if="field.shallowRender"
                          v-model:[field.vModelField!]="field.shallowValue"
                          :disabled="operation === 'view' || field.shallowDisabled"
                        ></component>
                        <DynamicElement
                          v-else-if="field.component"
                          :key="field.cacheKey"
                          v-model:value="field.shallowValue"
                          :v-model-field="field.vModelField"
                          :form-data="model"
                          :original-data="originalModel"
                          :component="field.component?.type"
                          :component-props="field.component?.props"
                          :watch-field="field.watchField"
                          :component-slots="toValue(field.component?.slots as Record<string, VNode>)"
                          :register="field.component?.register"
                          :register-trigger="field.component?.registerTrigger"
                          :help-message="field.helpMessage"
                          :disabled="operation === 'view' || toValue(field.shallowDisabled)"
                          :show-component-when-disabled="ShowWhenDisabledComponents.includes(field.component?.type)"
                          :prefix="field.component?.prefix"
                          :suffix="field.component?.suffix"
                        ></DynamicElement>
                        <NTooltip v-if="field.helpMessage" trigger="hover">
                          {{ field.helpMessage }}
                          <template #trigger>
                            <NButton circle quaternary size="small" class="ml-4px" type="warning">
                              <template #icon>
                                <icon-mdi-information-slab-circle />
                              </template>
                            </NButton>
                          </template>
                        </NTooltip>
                      </template>
                    </slot>
                  </template>
                </NFormItemGi>
              </template>
            </NGrid>
            <NDivider v-if="groupIndex !== groups.length - 1" />
          </template>
        </template>
      </template>
      <NGrid v-else responsive="screen" x-gap="12px" item-responsive>
        <template v-for="field in formFields" :key="field.cacheKey">
          <NFormItemGi
            v-if="field.shallowIfShow"
            :key="field.key"
            :span="field.shallowColSpan"
            :label="field.title"
            :label-placement="'labelPlacement' in field ? field.labelPlacement : undefined"
            :label-align="'labelAlign' in field ? field.labelAlign : undefined"
            :label-width="'labelWidth' in field ? field.labelWidth : undefined"
            :label-style="'labelStyle' in field ? field.labelStyle : undefined"
            :path="toValue(field.rulePath)"
            :show-label="field.showLabel"
            :show-feedback="'showFeedback' in field ? field.showFeedback : true"
            :rule="toValue(field.shallowRules)"
          >
            <NSkeleton v-if="loading && action === 'fetch'" :sharp="false" width="100%" height="32px"></NSkeleton>
            <template v-else-if="show">
              <slot
                :name="'field-' + field.key"
                :model="model"
                :original-model="originalModel"
                :loading="loading"
                :operation="operation"
                :value="_.get(model, field.key)"
              >
                <template v-if="field.type !== 'slot'">
                  <component
                    :is="field.shallowRender"
                    v-if="field.shallowRender"
                    v-model:[field.vModelField!]="field.shallowValue"
                    :disabled="operation === 'view' || field.shallowDisabled"
                  ></component>
                  <DynamicElement
                    v-else-if="field.component"
                    :key="field.cacheKey"
                    v-model:value="field.shallowValue"
                    :form-data="model"
                    :original-data="originalModel"
                    :v-model-field="field.vModelField"
                    :component="field.component?.type"
                    :component-props="field.component?.props"
                    :component-slots="toValue(field.component?.slots as Record<string, VNode>)"
                    :watch-field="field.watchField"
                    :register-trigger="field.component?.registerTrigger"
                    :register="field.component?.register"
                    :help-message="field.helpMessage"
                    :disabled="operation === 'view' || toValue(field.shallowDisabled)"
                    :show-component-when-disabled="ShowWhenDisabledComponents.includes(field.component?.type)"
                    :prefix="field.component?.prefix"
                    :suffix="field.component?.suffix"
                  ></DynamicElement>
                  <NTooltip v-if="field.helpMessage" trigger="hover">
                    {{ field.helpMessage }}
                    <template #trigger>
                      <NButton circle quaternary size="small" class="ml-4px" type="warning">
                        <template #icon>
                          <icon-mdi-information-slab-circle />
                        </template>
                      </NButton>
                    </template>
                  </NTooltip>
                </template>
              </slot>
            </template>
          </NFormItemGi>
        </template>
      </NGrid>
      <NSpace v-if="showActions" :size="16" justify="end">
        <slot name="actions" :loading="loading" :model="model">
          <NButton type="primary" @click="handleSubmit">{{ $t('common.confirm') }}</NButton>
        </slot>
      </NSpace>
    </NForm>
  </NSpin>
</template>

<style>
.form-view .n-form-item .n-form-item-feedback-wrapper {
  min-height: 12px;
}
</style>
